import React from 'react';
import { ApolloError } from '@apollo/client';
import { useGetJobsiteWorkerDocumentsQuery } from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import {
  JobsiteWorkerDocumentType,
  JobsiteWorkerOnboardingDocuments,
  UseJobsiteWorkerOnboardingDocumentsArgs,
} from 'containers/workerOnboarding/types';
import { to } from 'acl';
import { useJobsiteWorkerDocumentTypes } from './useJobsiteWorkerDocumentTypes';

export function useJobsiteWorkerOnboardingDocuments(
  args: UseJobsiteWorkerOnboardingDocumentsArgs,
): JobsiteWorkerOnboardingDocuments {
  const { jobsiteWorker } = args;
  const { currentUser: user } = React.useContext(AuthContext);

  const {
    jobsiteWorkerDocumentTypes,
    error: jobsiteWorkerDocumentTypesError,
    loading: jobsiteWorkerDocumentTypesLoading,
  } = useJobsiteWorkerDocumentTypes(
    jobsiteWorker?.jobsiteContractor?.jobsite?.jobsiteId,
  );

  const {
    data: jobsiteWorkerDocumentsData,
    error: jobsiteWorkerDocumentsError,
    loading: jobsiteWorkerDocumentsLoading,
    refetch: refetchJobsiteWorkerDocuments,
  } = useGetJobsiteWorkerDocumentsQuery({
    variables: {
      jobsiteWorkerId: jobsiteWorker?.jobsiteWorkerId,
      documentsInput: {
        includePhi: user.isAllowed(
          to.accessPhiDocuments,
          jobsiteWorker?.jobsiteContractor?.jobsite?.jobsiteId,
        ),
      },
    },
    fetchPolicy: 'no-cache',
    skip: !jobsiteWorker || !user.roles,
    notifyOnNetworkStatusChange: true,
  });

  const loading: boolean =
    jobsiteWorkerDocumentsLoading || jobsiteWorkerDocumentTypesLoading;

  const error: ApolloError =
    jobsiteWorkerDocumentsError || jobsiteWorkerDocumentTypesError;

  const jobsiteWorkerDocuments = React.useMemo(
    () =>
      jobsiteWorkerDocumentsData?.getJobsiteWorker?.documents?.edges.map(
        ({ node }) => node,
      ) || [],
    [jobsiteWorkerDocumentsData],
  );

  const filteredDocumentTypeIds: Record<string, string> = React.useMemo(
    () =>
      jobsiteWorkerDocumentTypes.reduce(
        (result, jwDocumentType: JobsiteWorkerDocumentType) => ({
          ...result,
          [jwDocumentType?.key ?? jwDocumentType?.workerDocumentType?.key]:
            jwDocumentType?.id,
        }),
        {},
      ),
    [jobsiteWorkerDocumentTypes],
  );

  return {
    loading,
    error,
    jobsiteWorkerDocumentTypes,
    jobsiteWorkerDocuments,
    filteredDocumentTypeIds,
    refetchJobsiteWorkerDocuments,
  };
}
